import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import LoadingSpinner from '../../../shared/components/UIElements/LoadingSpinner';
import { useHttpClient } from '../../../shared/hooks/http-hook';

const PASSCODE_LENGTH = 4;

type InputState = 'default' | 'confirmed' | 'wrong';

interface PasscodeProps {
  // eslint-disable-next-line no-unused-vars
  handleContinue: (code: string) => void;
  confirm?: boolean;
  passcode?: string;
  errorMessage?: string;
}

function Passcode({ handleContinue, confirm, passcode, errorMessage }: PasscodeProps) {
  const navigate = useNavigate();
  const { isLoading } = useHttpClient();
  const { t } = useTranslation();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [inputState, setInputState] = useState<InputState>('default');

  const isValidPasscode = code.length === PASSCODE_LENGTH;
  const isButtonAccepted = isValidPasscode && inputState !== 'wrong';

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isNumeric = /^\d*$/.test(value);

    if (isNumeric && value.length <= PASSCODE_LENGTH) {
      setCode(value);
      setError('');
      setInputState('default');
    }
  };

  const handleNavigation = () => {
    navigate('/reset', {
      state: { passcode: true },
    });
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!isValidPasscode) {
      setError(t('signin.passcode.error'));
      return;
    }

    if (!confirm) {
      handleContinue(code);
    } else if (passcode === code) {
      setInputState('confirmed');
      timeoutRef.current = setTimeout(() => {
        handleContinue(code);
      }, 2000);
    } else {
      setError(t('signup.passcode.does_not_match'));
      setInputState('wrong');
    }
  };

  useEffect(() => {
    if (errorMessage && errorMessage.length > 0) {
      setError(errorMessage);
      timeoutRef.current = setTimeout(() => {
        setCode('');
      }, 2000);
    }
  }, [errorMessage]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <label className="not_logged_label" htmlFor="passcode">
        {t('signup.passcode.label')}
      </label>
      <div className="passcode">
        <input
          type="password"
          className={`code_input passcode ${inputState}`}
          id="passcode"
          name="passcode"
          minLength={PASSCODE_LENGTH}
          maxLength={PASSCODE_LENGTH}
          onChange={handleValueChange}
          value={code}
          aria-label={t('signup.passcode.label')}
          data-testid="passcode-input"
        />
      </div>
      {isLoading && <LoadingSpinner asOverlay />}
      {error && (
        <Alert
          type="error"
          message={error}
          id="passcode-error"
          dataTestId="passcode-error"
          isVisible
          isAssessive
        />
      )}
      {!isLoading && (
        <Button
          type="submit"
          id="continue"
          className={isButtonAccepted ? 'accepted' : 'declined'}
          dataTestId="passcode-submit-button"
        >
          {t('common.continue')}
        </Button>
      )}
      <Button
        className="signin"
        onClick={handleNavigation}
        variant="secondary"
        type="button"
        dataTestId="forgot-passcode-button"
      >
        {t('signin.forgot.passcode')}
      </Button>
    </form>
  );
}

export default Passcode;
